import type { AlertErrorHandlerFactory } from '@/features/shell'
import { ViewModelList } from '@/infra/view-model-list'
import { Id } from '@/infra/id'
import type { GetRelationManagerFn } from '@/features/relation-manager'
import { type JobCollaboratorsViewModel } from '@/features/jobs/details/JobCollaboratorsViewModel'
import { type Job } from '@/features/jobs/Job'
import { type JobNote } from '@/features/jobs/details/notes/JobNote'
import { type JobNoteStore } from '@/features/jobs/details/notes/JobNoteStore'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { JobNoteItemViewModel } from '@/features/jobs/details/components/JobNotesCard/JobNoteItem/JobNoteItemViewModel'
import { DeleteJobNoteDialogViewModel } from './DeleteJobNoteDialogViewModel'
import {
  AddJobNoteDialogViewModel,
  type JobNoteRecipient,
} from '@/features/jobs/details/components/JobNotesCard/AddJobNoteDialog/AddJobNoteDialogViewModel'
import { type Member } from '@/features/iam/members/Member'
import { action, computed, observable } from 'mobx'
import { delay, filter, switchMap, tap } from 'rxjs/operators'
import { withAbortSignal } from '@/utils/rx-util'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { ActivationLifecycle } from '@/infra/activation'

// How long a note revealed from the chat stays highlighted, in ms.
const HIGHLIGHT_LINGER = 4000

const notesPageSize = 20

/**
 * View model for the job notes card.
 */
export class JobNotesViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Note view models newest first.
   */
  private readonly _notesList = new ViewModelList({
    models: () => this._store.notesForJob(this._job.id),
    create: (note: JobNote) =>
      new JobNoteItemViewModel(note, (userPublicId) =>
        this.noteMentionLabel(userPublicId),
      ),
  })

  /**
   * Dialog confirming a note's deletion.
   */
  readonly deleteNoteDialogVm

  /**
   * Notice telling the user the notes moved out of the chat.
   */
  readonly movedNotice = new ModalViewModel()

  /**
   * Whether the notes are highlighted after being revealed from the chat.
   */
  @observable accessor isHighlighted = false

  /**
   * Dialog for adding a note.
   */
  readonly addNoteVm

  /**
   * Pages through the job's notes.
   */
  readonly scroller = new InfiniteScrollProvider<JobNote>({
    limit: notesPageSize,
    fetchItems: ({ paging, signal }) =>
      this._store.listJobNotes({ jobId: this._job.id, ...paging }, signal),
  })

  constructor(
    private readonly _job: Job,
    private readonly _store: JobNoteStore,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _getWorkspaceMember: () => Member,
    private readonly _collaboratorsVm: JobCollaboratorsViewModel,
    private readonly _getRelationManager: GetRelationManagerFn,
    private readonly _fetchRelationManager: () => Promise<void>,
  ) {
    this.deleteNoteDialogVm = new DeleteJobNoteDialogViewModel(
      this._store,
      this._createAlertErrorHandler,
    )

    this.addNoteVm = new AddJobNoteDialogViewModel(
      async (body, recipientUserPublicIds) => {
        await this._store.createJobNote({
          jobId: this._job.id,
          body,
          recipientUserPublicIds,
        })
        await this.scroller.fetch({
          signal: this._lifecycle.signal,
          staleWhileRevalidate: true,
        })
      },
      this._createAlertErrorHandler,
      () => this.notifiableRecipients,
    )

    this._lifecycle
      .manage(this._notesList)
      .manage(
        this.movedNotice.close$.pipe(
          delay(HIGHLIGHT_LINGER),
          tap(
            action(() => {
              this.isHighlighted = false
            }),
          ),
        ),
      )
      .manage(
        this._store.events$.pipe(
          filter(
            (event) =>
              event.jobId === this._job.id && event.type === 'JobNoteCreated',
          ),
          switchMap(() =>
            withAbortSignal((signal) =>
              // The list renders a failed refetch from the scroller's state.
              this.scroller
                .fetch({ signal, staleWhileRevalidate: true })
                .catch(() => undefined),
            ),
          ),
        ),
      )
  }

  /**
   * Note item view models, newest first.
   */
  @computed
  get notes() {
    return this._notesList.items
  }

  /**
   * Whether the notes are refetching while the previous ones stay on screen.
   */
  @computed
  get isRevalidating() {
    return this.scroller.isRevalidating
  }

  /**
   * Whether the first page of notes is still loading.
   */
  @computed
  get isFetchingInitial() {
    return (
      this.scroller.fetch.pending &&
      !this.scroller.isRevalidating &&
      // The store keeps the job's notes from an earlier visit, and those stay
      // up while the activation fetch resets the scroller.
      this.notes.length === 0
    )
  }

  /**
   * How many notes there are, marked open-ended while more pages remain.
   */
  @computed
  get countLabel(): string | undefined {
    const count = this.notes.length
    if (count === 0) {
      return undefined
    }
    return this.scroller.isDone || count < notesPageSize
      ? `${count}`
      : `${count}+`
  }

  /**
   * Whether the current user may see the notes.
   */
  @computed
  get canView() {
    return this._job.isProviderCollaborator || this._canManage
  }

  /**
   * Whether the current user may add notes.
   */
  @computed
  get canWrite() {
    return (
      !this._job.completed &&
      (this._job.isProviderCollaborator || this._canManage)
    )
  }

  /**
   * Participants the current user may notify when adding a note.
   */
  @computed
  get notifiableRecipients(): JobNoteRecipient[] {
    const rm = this._getRelationManager(this._job.id)
    const rmPublicId = rm?.user.publicId

    const rmOption: JobNoteRecipient | null =
      rm && rmPublicId
        ? { userPublicId: rmPublicId, name: rm.displayName, role: 'RM' }
        : null

    const proOptions: JobNoteRecipient[] = this._collaboratorsVm.collaborators
      .filter(
        (c) =>
          c.type === 'Pro' &&
          !c.isCurrentUser &&
          Id.some(c.member.user.publicId),
      )
      .map((c): JobNoteRecipient => ({
        userPublicId: c.member.user.publicId,
        name: c.member.displayName,
        role: 'Pro',
      }))

    if (this._job.isProviderCollaborator) {
      return rmOption ? [rmOption] : []
    }

    if (rm?.isCurrentUser) {
      return proOptions
    }

    if (this._canManage) {
      return [...(rmOption ? [rmOption] : []), ...proOptions]
    }

    return []
  }

  /**
   * Whether the current user may delete the note.
   */
  canDelete(note: JobNoteItemViewModel) {
    return (
      this._canManage ||
      (this._job.isProviderCollaborator && note.author.isCurrentUser)
    )
  }

  /**
   * The label shown for a notified recipient on an existing note.
   */
  noteMentionLabel(userPublicId: Id): string | undefined {
    const rm = this._getRelationManager(this._job.id)
    if (rm && rm.user.publicId === userPublicId) {
      return '@RM'
    }

    const provider = this._collaboratorsVm.collaborators.find(
      (c) => c.type === 'Pro' && c.member.user.publicId === userPublicId,
    )
    return provider ? `@${provider.member.displayName}` : undefined
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The relation manager is optional context; the notes work without it.
    void this._fetchRelationManager().catch(() => undefined)
    // The notes render a failure from the task's state.
    void this.scroller
      .fetch({ signal: this._lifecycle.signal, staleWhileRevalidate: true })
      .catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Highlights the notes when the chat points the user to them.
   */
  @action.bound
  revealFromChat() {
    this.isHighlighted = true
    this.movedNotice.show()
  }

  /**
   * Asks to confirm, then deletes the note.
   */
  @action.bound
  async deleteNote(note: JobNoteItemViewModel) {
    await this.deleteNoteDialogVm.prompt(note.id, this._lifecycle.signal)
  }

  private get _canManage() {
    return this._getWorkspaceMember().permissions.workspaceJobNoteManage
  }
}
